import { get } from 'lodash';
import Cookie from 'js-cookie';
import { ELSTokenHelper, ELSURLHelper } from '@els/els-ui-common-react';
import { AppLinkData } from '../../apis/eols-app-link/eols-app-link.dtos';
import {
  AppAction,
  Application
} from '../../apis/eols-app-link/eols-app-link.constants';
import { APP_LINK_COOKIE_EXPIRATION_DAYS, AppLinkCookie } from './app-link-redirect.constants';
import { ServerConstants } from '../../components/app/server.constants';
import { ELSCommonConfig } from '../../components/els.components';
import { ActiveSyllabusItemTypeDto, SyllabusItemTypeDto } from '../../apis/sherpath-syllabus-service/sherpath-syllabus-service.constants';
import { FeatureFlagsGroupedDto } from '../../apis/eols-features-api/eols-features-api.dtos';
import { AssignmentDto } from '../../apis/eols-assessment-service/eols-assessment-service.dtos';
import { getBooleanFromGroupFeatureFlagWithFallbackToGlobal } from '../../utilities/featureFlag.utilities';
import { FEATURE_FLAG } from '../../apis/eols-features-api/eols-features-api.constants';

const getUpdatedAssignmentIdsFromAppLinkData = (appLinkData: AppLinkData): string[] | number[] => {
  return get(appLinkData, 'returnPostBody.assignmentIds', []) as string[] | number[];
};

export const shouldConnectAssignmentToSyllabusItem = (appLinkData: AppLinkData): boolean => {
  if (appLinkData.action !== AppAction.ASSIGNMENT_CREATE) {
    return false;
  }
  const assignmentIds = getUpdatedAssignmentIdsFromAppLinkData(appLinkData);
  const syllabusItemId = get(appLinkData, 'outPostBody.syllabusItemId', false);
  return Boolean(assignmentIds.length === 1 && syllabusItemId);
};

export const shouldCreateNewSyllabusItemsForNewAssignments = (appLinkData: AppLinkData): boolean => {
  if (appLinkData.action !== AppAction.ASSIGNMENT_CREATE) {
    return false;
  }
  const assignmentIds = getUpdatedAssignmentIdsFromAppLinkData(appLinkData);
  const parentSyllabusItemId = get(appLinkData, 'outPostBody.parentSyllabusItemId', false);
  const syllabusItemId = get(appLinkData, 'outPostBody.syllabusItemId', false);
  return Boolean(assignmentIds.length >= 1 && !syllabusItemId && parentSyllabusItemId);
};

export const shouldShowAssignmentUpdatedToast = (appLinkData: AppLinkData): boolean => {
  // This is tech debt that should be cleaned up
  // EAQ should not be expecting us to treat preview actions like edits
  const isEAQEdit = appLinkData.targetApp === Application.EAQ && appLinkData.action === AppAction.ASSIGNMENT_PREVIEW;
  if (appLinkData.action === AppAction.ASSIGNMENT_EDIT || isEAQEdit) {
    const assignmentIds = get(appLinkData, 'returnPostBody.assignmentIds', []);
    return assignmentIds.length > 0;
  }
  return false;
};

export const addCookies = (linkId: string) => {

  const token = ELSTokenHelper.getToken();
  const domain = ServerConstants[ServerConstants.DataSource].cookiesDomain;

  Cookie.set(AppLinkCookie.X_LINK_ID, linkId, { path: '/', domain, expires: APP_LINK_COOKIE_EXPIRATION_DAYS });
  Cookie.set(AppLinkCookie.X_TOKEN, token, { path: '/', domain, expires: APP_LINK_COOKIE_EXPIRATION_DAYS });
};

export const removeCookies = () => {
  const domain = ServerConstants[ELSCommonConfig.appProfile].cookiesDomain;
  Cookie.remove(AppLinkCookie.X_LINK_ID, { path: '/', domain });
  Cookie.remove(AppLinkCookie.X_TOKEN, { path: '/', domain });
};

export const pickCookies = (): {
  token: string;
  linkId: string;
} => {

  const tokenFromUrl = ELSURLHelper.getParameterByName('token');
  const linkIdFromUrl = ELSURLHelper.getParameterByName('linkId');

  if (tokenFromUrl && linkIdFromUrl) {

    removeCookies();

    return {
      token: tokenFromUrl,
      linkId: linkIdFromUrl
    };
  }

  const token = Cookie.get(AppLinkCookie.X_TOKEN);
  const linkId = Cookie.get(AppLinkCookie.X_LINK_ID);

  removeCookies();

  return { token, linkId };
};

// TODO: we can remove this function once we migrate all the assignment types
const syllabusItemTypeToAppHandlerMap: Partial<Record<SyllabusItemTypeDto, (
  featureFlagsGrouped: FeatureFlagsGroupedDto[],
  courseSectionId: string,
  assignment: AssignmentDto
) => Application>> = {
  [ActiveSyllabusItemTypeDto.SHERPATH_SIMULATIONS]: () => Application.SHER_EVOL,
  [ActiveSyllabusItemTypeDto.SHERPATH_SKILL]: () => Application.SHER_EVOL,
  [ActiveSyllabusItemTypeDto.ASSESSMENT_BUILDER_ASSIGNMENT]: () => Application.EAB,
  [ActiveSyllabusItemTypeDto.INTERACTIVE_REVIEW]: (featureFlagsGrouped: FeatureFlagsGroupedDto[], courseSectionId: string) => {
    const isInteractiveReviewModalEnabled = featureFlagsGrouped && courseSectionId ? getBooleanFromGroupFeatureFlagWithFallbackToGlobal(
      featureFlagsGrouped,
      FEATURE_FLAG.ENABLE_INTERACTIVE_REVIEW_MODAL,
      courseSectionId
    ) : false;
    return isInteractiveReviewModalEnabled ? Application.NHE_ASSESSMENT_PLAYER : Application.NCO;
  },
  [ActiveSyllabusItemTypeDto.PRE_SIMULATION_QUIZ]: () => Application.NHE_ASSESSMENT_PLAYER,
  [ActiveSyllabusItemTypeDto.POST_SIMULATION_QUIZ]: () => Application.NHE_ASSESSMENT_PLAYER,
  [ActiveSyllabusItemTypeDto.SCMO_NG]: () => Application.SCMO,
  [ActiveSyllabusItemTypeDto.PRE_SIMULATION_EXERCISE]: () => Application.NHE_ASSESSMENT_PLAYER,
  [ActiveSyllabusItemTypeDto.POST_SIMULATION_EXERCISE]: () => Application.NHE_ASSESSMENT_PLAYER,
  [ActiveSyllabusItemTypeDto.JOURNAL_EXERCISE]: () => Application.NHE_ASSESSMENT_PLAYER,
  [ActiveSyllabusItemTypeDto.SBAR_EXERCISE]: () => Application.NHE_ASSESSMENT_PLAYER
};

// TODO: we can remove this function once we migrate all the assignment types
const syllabusItemTypeToAppForAssignmentPerformanceHandlerMap: Partial<Record<SyllabusItemTypeDto, (
  featureFlagsGrouped: FeatureFlagsGroupedDto[],
  courseSectionId: string,
  assignment: AssignmentDto
) => Application>> = {
  [ActiveSyllabusItemTypeDto.SHERPATH_SIMULATIONS]: () => Application.SHER_EVOL,
  [ActiveSyllabusItemTypeDto.SHERPATH_SKILL]: () => Application.SHER_EVOL,
  [ActiveSyllabusItemTypeDto.ASSESSMENT_BUILDER_ASSIGNMENT]: () => Application.EAB,
  [ActiveSyllabusItemTypeDto.INTERACTIVE_REVIEW]: () => Application.ASSIGNMENT_MANAGEMENT,
  [ActiveSyllabusItemTypeDto.PRE_SIMULATION_QUIZ]: () => Application.ASSIGNMENT_MANAGEMENT,
  [ActiveSyllabusItemTypeDto.POST_SIMULATION_QUIZ]: () => Application.ASSIGNMENT_MANAGEMENT,
  [ActiveSyllabusItemTypeDto.SCMO_NG]: () => Application.SCMO,
  [ActiveSyllabusItemTypeDto.PRE_SIMULATION_EXERCISE]: () => Application.ASSIGNMENT_MANAGEMENT,
  [ActiveSyllabusItemTypeDto.POST_SIMULATION_EXERCISE]: () => Application.ASSIGNMENT_MANAGEMENT,
  [ActiveSyllabusItemTypeDto.SBAR_EXERCISE]: () => Application.ASSIGNMENT_MANAGEMENT,
  [ActiveSyllabusItemTypeDto.JOURNAL_EXERCISE]: () => Application.ASSIGNMENT_MANAGEMENT,
};

// TODO: we can remove this function once we migrate all the assignment types
export const getAppLinkAppForSyllabusItemType = (
  syllabusItemType: SyllabusItemTypeDto,
  action: AppAction,
  featureFlagsGrouped: FeatureFlagsGroupedDto[],
  courseSectionId: string,
  assignment: AssignmentDto
): Application => {
  if (action === AppAction.ASSIGNMENT_PERFORMANCE_VIEW) {
    const applicationForAssignmentPerformanceHandler = syllabusItemTypeToAppForAssignmentPerformanceHandlerMap[syllabusItemType];
    return applicationForAssignmentPerformanceHandler(featureFlagsGrouped, courseSectionId, assignment) || Application.SHER_EVOL;
  }
  const applicationHandler = syllabusItemTypeToAppHandlerMap[syllabusItemType];
  return applicationHandler(featureFlagsGrouped, courseSectionId, assignment) || Application.SHER_EVOL;
};
